import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  TextInput,
  Alert,
  Spinner,
  Badge,
  Pagination,
  Datepicker,
} from 'flowbite-react';
import Select2 from 'react-select';
import { fetchWithAuth } from '../utils/api';
import { getCampaignsEndpoints, getOrdersEndpoints } from '../config/api';
import {
  getValidationMessages,
  formatDate,
  formatCurrency,
} from '../utils/helper';
import { loadCampaignsList } from '../config/lib';
import Swal from 'sweetalert2';

interface Order {
  id: number;
  order_id: string;
  customer_id: string;
  total_amount: number;
  original_amount: number;
  products?: string[];
  match_product?: boolean;
  campaign: number;
  campaign_name?: string;
  created_at: string;
}

interface Campaign {
  id: number;
  name: string;
}

interface OrderFormData {
  order_id: string;
  customer_id: string;
  total_amount: number | null;
  original_amount: number | null;
  products: string[];
  campaign: number;
  created_at: string;
}

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);
  const [messagePopup, setMessagePopup] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [campaignFilter, setCampaignFilter] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const itemsPerPage = 10;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  const [showModalCalculateRevenue, setShowModalCalculateRevenue] =
    useState(false);
  const [calculateRevenueDate, setCalculateRevenueDate] = useState<Date | null>(
    null
  );

  // Form states
  const [formData, setFormData] = useState<OrderFormData>({
    order_id: '',
    customer_id: '',
    total_amount: null,
    original_amount: null,
    products: [],
    campaign: 0,
    created_at: formatDate(new Date()),
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Remove non-numeric characters
  const removeNonNumeric = (value: string) => {
    return value.replace(/[^0-9]/g, '');
  };

  // Load orders
  const loadOrders = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const ordersEndpoint = getOrdersEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(campaignFilter && { campaign: campaignFilter.value.toString() }),
        ...(dateFromFilter && { date_from: formatDate(dateFromFilter) }),
        ...(dateToFilter && { date_to: formatDate(dateToFilter) }),
      });

      const response = await fetchWithAuth(`${ordersEndpoint}?${params}`);

      if (response.success && response.data) {
        setOrders(response.data.results || []);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
        setTotalAmount(response.data.meta.total_amount || 0);
      } else {
        setError('Không thể tải danh sách orders');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCampaignFilter(null);
    setDateFromFilter(null);
    setDateToFilter(null);
  };

  // Load campaigns
  const loadCampaigns = async () => {
    const campaignList = await loadCampaignsList();
    setCampaigns(campaignList);
  };

  useEffect(() => {
    loadOrders();
    loadCampaigns();
  }, []);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    loadOrders(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadOrders(page);
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setEditingOrder(null);
    setFormData({
      order_id: '',
      customer_id: '',
      total_amount: null,
      original_amount: null,
      products: [],
      campaign: 0,
      created_at: formatDate(new Date()),
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Open edit modal
  const openEditModal = (order: Order) => {
    setModalMode('edit');
    setEditingOrder(order);
    setFormData({
      order_id: order.order_id,
      customer_id: order.customer_id,
      total_amount: order.total_amount,
      original_amount: order.original_amount,
      products: order.products?.length ? [...order.products] : [],
      campaign: order.campaign,
      created_at: order.created_at,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  const openModalCalculateRevenue = () => {
    setShowModalCalculateRevenue(true);
  };

  const handleCalculateRevenue = async () => {
    if (!calculateRevenueDate) {
      setMessagePopup({
        type: 'failure',
        text: 'Ngày tính doanh thu là bắt buộc',
      });
      return;
    }
    setSubmitting(true);
    const calculateRevenueEndpoint = getOrdersEndpoints().CALCULATE_REVENUE;
    const response = await fetchWithAuth(calculateRevenueEndpoint, {
      method: 'POST',
      body: JSON.stringify({
        date: formatDate(calculateRevenueDate),
      }),
    });
    if (response.success) {
      Swal.fire({
        icon: 'success',
        title: 'Thông báo!',
        text: `Đã xử lý tính doanh thu cho ngày ${formatDate(
          calculateRevenueDate
        )}. Có thể mất vài phút để hoàn thành...`,
      });
      setShowModalCalculateRevenue(false);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Thông báo!',
        text: 'Không thể tính lại doanh thu',
      });
    }
    setSubmitting(false);
  };

  const handleProductChange = (index: number, value: string) => {
    const newProducts = [...formData.products];
    newProducts[index] = value;
    setFormData(prev => ({ ...prev, products: newProducts }));
  };

  const addProduct = () => {
    setFormData(prev => ({ ...prev, products: [...prev.products, ''] }));
  };

  const removeProduct = (index: number) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
  };

  // Handle form input change
  const handleInputChange = (field: keyof OrderFormData, value: any) => {
    // Convert string to number for total_amount
    if (field === 'total_amount') {
      const numValue = value === '' || value === null ? null : Number(value);
      setFormData(prev => ({ ...prev, [field]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.order_id.trim()) {
      errors.order_id = 'Order ID là bắt buộc';
    } else if (formData.order_id.length > 50) {
      errors.order_id = 'Order ID không được vượt quá 50 ký tự';
    }

    if (!formData.customer_id.trim()) {
      errors.customer_id = 'Customer ID là bắt buộc';
    } else if (formData.customer_id.length > 100) {
      errors.customer_id = 'Customer ID không được vượt quá 100 ký tự';
    }

    if (formData.total_amount === null || formData.total_amount === undefined) {
      errors.total_amount = 'Tổng tiền là bắt buộc';
    } else if (formData.total_amount < 0) {
      errors.total_amount = 'Tổng tiền phải lớn hơn hoặc bằng 0';
    }

    if (
      formData.original_amount === null ||
      formData.original_amount === undefined
    ) {
      errors.original_amount = 'Tiền gốc là bắt buộc';
    } else if (formData.original_amount < 0) {
      errors.original_amount = 'Tiền gốc phải lớn hơn hoặc bằng 0';
    }

    if (!formData.campaign || formData.campaign === 0) {
      errors.campaign = 'Campaign là bắt buộc';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const ordersEndpoint = getOrdersEndpoints();
      const endpoint =
        modalMode === 'create'
          ? ordersEndpoint.CREATE
          : `${ordersEndpoint.UPDATE}/${editingOrder?.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify({
          order_id: formData.order_id.trim(),
          customer_id: formData.customer_id.trim(),
          total_amount: formData.total_amount,
          original_amount: formData.original_amount,
          products: formData.products
            .map(p => p.trim())
            .filter(p => p !== ''),
          campaign: formData.campaign,
          created_at: formData.created_at,
        }),
      });

      if (response.success) {
        setMessagePopup({
          type: 'success',
          text: `Order ${
            modalMode === 'create' ? 'đã được tạo' : 'đã được cập nhật'
          } thành công!`,
        });
        setShowModal(false);
        loadOrders(currentPage);
      } else {
        setMessagePopup({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            `Không thể ${modalMode === 'create' ? 'tạo' : 'cập nhật'} order`,
        });
      }
    } catch (error) {
      setMessagePopup({
        type: 'failure',
        text: `Đã xảy ra lỗi khi ${
          modalMode === 'create' ? 'tạo' : 'cập nhật'
        } order`,
      });
      console.error('Submit order error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (orderId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa order này?')) {
      return;
    }

    try {
      const ordersEndpoint = getOrdersEndpoints();
      const response = await fetchWithAuth(
        `${ordersEndpoint.DELETE}/${orderId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Order đã được xóa thành công!',
        });
        loadOrders(currentPage);
      } else {
        setMessage({
          type: 'failure',
          text: 'Không thể xóa order',
        });
      }
    } catch (error) {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi xóa order',
      });
      console.error('Delete order error:', error);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải danh sách orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
            <p className="text-gray-600">Quản lý các orders</p>
          </div>
          <div className="flex space-x-2">
            <Button onClick={openCreateModal} className="btn-primary">
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Tạo Order
            </Button>
            <Button
              className="btn-success"
              onClick={() => openModalCalculateRevenue()}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              Tính lại doanh thu
            </Button>
          </div>
        </div>
      </div>

      {message && (
        <Alert color={message.type} className="mb-4">
          <span>{message.text}</span>
        </Alert>
      )}

      {/* Search and Filter */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <TextInput
              placeholder="Tìm kiếm theo Order ID hoặc Customer ID..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Select2
              styles={{
                input: base => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 'none',
                  border: 'none',
                }),
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  height: '42px',
                }),
              }}
              options={campaigns.map(campaign => ({
                value: campaign.id,
                label: campaign.name,
              }))}
              onChange={ob => setCampaignFilter(ob)}
              value={
                campaignFilter
                  ? {
                      value: campaignFilter?.value,
                      label: campaignFilter?.label,
                    }
                  : null
              }
              isClearable
              placeholder="Chọn Campaign..."
            />
            <Datepicker
              icon={undefined}
              value={dateFromFilter ? formatDate(dateFromFilter) : ''}
              title="Từ ngày"
              placeholder="Từ ngày"
              onSelectedDateChanged={e => {
                setDateFromFilter(e);
              }}
              showClearButton={false}
              maxDate={dateToFilter || new Date()}
              theme={{
                views: {
                  days: {
                    items: {
                      base: 'grid w-64 grid-cols-7',
                      item: {
                        base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                        selected:
                          'bg-primary-700 text-blue-500 hover:bg-primary-600',
                        disabled: 'text-gray-500 line-through',
                      },
                    },
                  },
                },
              }}
            />
            <Datepicker
              icon={undefined}
              value={dateToFilter ? formatDate(dateToFilter) : ''}
              title="Đến ngày"
              placeholder="Đến ngày"
              onSelectedDateChanged={e => {
                setDateToFilter(e);
              }}
              showClearButton={false}
              minDate={dateFromFilter || undefined}
              maxDate={new Date()}
              theme={{
                views: {
                  days: {
                    items: {
                      base: 'grid w-64 grid-cols-7',
                      item: {
                        base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                        selected:
                          'bg-primary-700 text-blue-500 hover:bg-primary-600',
                        disabled: 'text-gray-500 line-through',
                      },
                    },
                  },
                },
              }}
            />
          </div>
          <div className="mt-4 flex gap-4">
            <Button onClick={handleSearch} className="btn-primary">
              Tìm kiếm
            </Button>
            <Button
              onClick={handleClearFilters}
              type="button"
              className="btn-reset"
            >
              Xóa bộ lọc
            </Button>
          </div>
        </div>
      </Card>

      {/* Orders Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>Order ID</Table.HeadCell>
              <Table.HeadCell>Customer ID</Table.HeadCell>
              <Table.HeadCell>Sản phẩm</Table.HeadCell>
              <Table.HeadCell>Đúng Sản Phẩm ADS</Table.HeadCell>
              <Table.HeadCell>Campaign</Table.HeadCell>
              <Table.HeadCell>Tiền ghi nhận</Table.HeadCell>
              <Table.HeadCell>Tiền gốc</Table.HeadCell>
              <Table.HeadCell>Ngày tạo</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {orders.map(order => (
                <Table.Row
                  key={order.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {order.order_id}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {order.customer_id}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400 max-w-[200px]">
                    {order.products && order.products.length > 0 ? (
                      <div className="text-md whitespace-pre-line">
                        {order.products.join('\n')}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color={order.match_product ? 'green' : 'red'}>
                      {order.match_product ? 'Đúng' : 'Sai'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="blue">
                      {order.campaign_name || `Campaign ${order.campaign}`}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="yellow">
                      {formatCurrency(order.total_amount)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="yellow">
                      {formatCurrency(order.original_amount)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(order.created_at)}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => openEditModal(order)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="sm"
                        color="red"
                        onClick={() => handleDelete(order.id.toString())}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4">
          <div className="text-sm text-gray-700">
            Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
            {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
            {totalItems} kết quả
          </div>
          <div className="text-sm text-gray-700">
            Tổng số tiền:
            <span className="font-bold"> {formatCurrency(totalAmount)}</span>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="4xl">
        <Modal.Header>
          {modalMode === 'create' ? 'Tạo Order Mới' : 'Chỉnh Sửa Order'}
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {messagePopup && (
              <Alert color={messagePopup.type} className="mb-4">
                <span>{messagePopup.text}</span>
              </Alert>
            )}
            <div className="space-y-4">
              {/* Order ID and Customer ID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order ID *
                  </label>
                  <TextInput
                    value={formData.order_id}
                    onChange={e =>
                      handleInputChange('order_id', e.target.value)
                    }
                    placeholder="Nhập Order ID (tối đa 50 ký tự)"
                    maxLength={50}
                    color={formErrors.order_id ? 'failure' : undefined}
                    helperText={formErrors.order_id}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer ID *
                  </label>
                  <TextInput
                    value={formData.customer_id}
                    onChange={e =>
                      handleInputChange('customer_id', e.target.value)
                    }
                    placeholder="Nhập Customer ID (tối đa 100 ký tự)"
                    maxLength={100}
                    color={formErrors.customer_id ? 'failure' : undefined}
                    helperText={formErrors.customer_id}
                  />
                </div>
              </div>

              {/* Campaign */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign{' '}
                  {modalMode === 'edit' ? ' (Không thể chỉnh sửa)' : '*'}
                </label>
                <Select2
                  styles={{
                    input: base => ({
                      ...base,
                      boxShadow: 'none',
                      outline: 'none',
                      border: 'none',
                    }),
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      height: '42px',
                    }),
                  }}
                  options={campaigns.map(campaign => ({
                    value: campaign.id,
                    label: campaign.name,
                  }))}
                  onChange={e =>
                    handleInputChange(
                      'campaign',
                      parseInt(e?.value?.toString() || '0')
                    )
                  }
                  value={
                    formData.campaign
                      ? {
                          value: formData.campaign,
                          label:
                            campaigns.find(
                              campaign => campaign.id === formData.campaign
                            )?.name || '',
                        }
                      : null
                  }
                  isClearable
                  placeholder="Chọn Campaign..."
                  isDisabled={modalMode === 'edit'}
                />
                {formErrors.campaign && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.campaign}
                  </p>
                )}
              </div>

              {/* Products */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sản phẩm
                </label>
                {formData.products.map((product, index) => (
                  <div key={index} className="flex space-x-2 mb-2">
                    <TextInput
                      value={product}
                      onChange={e =>
                        handleProductChange(index, e.target.value)
                      }
                      placeholder={`Sản phẩm ${index + 1}`}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      color="red"
                      size="sm"
                      onClick={() => removeProduct(index)}
                    >
                      Xóa
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  color="blue"
                  size="sm"
                  onClick={addProduct}
                  className="mt-2"
                >
                  Thêm sản phẩm
                </Button>
              </div>

              {/* Total Amount and Created At */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tổng tiền *
                  </label>
                  <TextInput
                    type="text"
                    value={
                      formData.total_amount !== null &&
                      formData.total_amount !== undefined
                        ? formData.total_amount.toString()
                        : ''
                    }
                    onChange={e =>
                      handleInputChange(
                        'total_amount',
                        removeNonNumeric(e.target.value)
                      )
                    }
                    placeholder="Nhập tổng tiền"
                    color={formErrors.total_amount ? 'failure' : undefined}
                    helperText={formErrors.total_amount}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tiền gốc *
                  </label>
                  <TextInput
                    type="text"
                    value={
                      formData.original_amount !== null &&
                      formData.original_amount !== undefined
                        ? formData.original_amount.toString()
                        : ''
                    }
                    onChange={e =>
                      handleInputChange(
                        'original_amount',
                        removeNonNumeric(e.target.value)
                      )
                    }
                    placeholder="Nhập tiền gốc"
                    color={formErrors.original_amount ? 'failure' : undefined}
                    helperText={formErrors.original_amount}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ngày tạo
                  </label>
                  <Datepicker
                    value={formatDate(formData.created_at)}
                    defaultDate={new Date(formData.created_at)}
                    icon={undefined}
                    onSelectedDateChanged={e => {
                      handleInputChange('created_at', formatDate(e));
                    }}
                    color={formErrors.created_at ? 'failure' : undefined}
                    helperText={formErrors.created_at}
                    maxDate={new Date()}
                    theme={{
                      views: {
                        days: {
                          items: {
                            base: 'grid w-64 grid-cols-7',
                            item: {
                              base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                              selected:
                                'bg-primary-700 text-blue-500 hover:bg-primary-600',
                              disabled: 'text-gray-500 line-through',
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="gray"
              onClick={() => setShowModal(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Đang xử lý...
                </>
              ) : modalMode === 'create' ? (
                'Tạo Order'
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Modal Calculate Revenue */}
      <Modal
        show={showModalCalculateRevenue}
        onClose={() => setShowModalCalculateRevenue(false)}
        size="4xl"
      >
        <Modal.Header>Tính lại doanh thu</Modal.Header>
        <Modal.Body>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ngày tính doanh thu *
            </label>
            <div className="relative">
              <Datepicker
                value={
                  calculateRevenueDate ? formatDate(calculateRevenueDate) : ''
                }
                icon={undefined}
                onSelectedDateChanged={e => {
                  setCalculateRevenueDate(e);
                }}
                color={!calculateRevenueDate ? 'failure' : undefined}
                helperText={
                  !calculateRevenueDate
                    ? 'Ngày tính doanh thu là bắt buộc'
                    : undefined
                }
                maxDate={new Date()}
                theme={{
                  popup: {
                    root: {
                      base: 'fixed z-[9999] block pt-2',
                    },
                  },
                  views: {
                    days: {
                      items: {
                        base: 'grid w-64 grid-cols-7',
                        item: {
                          base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                          selected:
                            'bg-primary-700 text-blue-500 hover:bg-primary-600',
                          disabled: 'text-gray-500 line-through',
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            color="gray"
            onClick={() => setShowModalCalculateRevenue(false)}
          >
            Hủy
          </Button>
          <Button
            type="button"
            onClick={handleCalculateRevenue}
            disabled={submitting}
            className="btn-primary"
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Đang xử lý...
              </>
            ) : (
              'Tính lại doanh thu'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Orders;
